/**
 * Tiny hook that fetches the public /api/content/published feed and
 * returns the blended Insights data: static + DB-published items.
 *
 * Lives outside the static insights.js content file so React can refresh
 * the feed at runtime without a redeploy.
 */
import { useEffect, useState } from "react";
import {
  BLOG_POSTS as STATIC_POSTS,
  RESOURCES   as STATIC_RESOURCES,
} from "./insights";

const API = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = "hv_published_feed_v1";

function dedupeBySlug(staticArr, dbArr) {
  // DB items take precedence: published from the Studio is the source of
  // truth, so a slug collision favours the DB row.
  const seen = new Set();
  const out = [];
  for (const x of (dbArr || [])) {
    if (!x || !x.slug) continue;
    if (seen.has(x.slug)) continue;
    seen.add(x.slug);
    out.push(x);
  }
  for (const x of (staticArr || [])) {
    if (!x || !x.slug) continue;
    if (seen.has(x.slug)) continue;
    seen.add(x.slug);
    out.push(x);
  }
  return out;
}

export function usePublishedContent() {
  const cached = (() => {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      return raw ? JSON.parse(raw) : { posts: [], resources: [] };
    } catch { return { posts: [], resources: [] }; }
  })();

  const [feed, setFeed] = useState(cached);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/content/published`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        const next = { posts: d.posts || [], resources: d.resources || [] };
        setFeed(next);
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      })
      .catch(() => { /* graceful: keep static-only */ });
    return () => { cancelled = true; };
  }, []);

  return {
    posts:     dedupeBySlug(STATIC_POSTS,     feed.posts),
    resources: dedupeBySlug(STATIC_RESOURCES, feed.resources),
  };
}
